/**
 * Renderização leve de markdown (prose com ícones + tabelas) para o painel Orya.
 */

import React, { memo, useCallback } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import RelatoriosOryaScrollableTable from './RelatoriosOryaScrollableTable';
import { enrichOryaProseLine } from '../utils/relatoriosOryaDisplay';
import { formatOryaMarkdownTextForDisplay, formatOryaTableCellForDisplay } from '../utils/relatoriosOryaNumberFormat';
import { countMarkdownTables } from '../utils/relatoriosOryaMarkdownParse';
import { isCompleteTableRow } from '../utils/relatoriosOryaTableAssert';
import { downloadOryaMarkdownTableXlsx } from '../utils/relatoriosOryaTableExport';

function parseMarkdownImage(line) {
  const trimmed = String(line || '').trim();
  const linked = trimmed.match(/^\[!\[([^\]]*)\]\(([^)]+)\)\]\(([^)]+)\)$/);
  if (linked) {
    return { alt: linked[1], src: linked[2], href: linked[3] };
  }
  const simple = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
  if (simple) {
    return { alt: simple[1], src: simple[2], href: null };
  }
  return null;
}

function extractMarkdownImages(text) {
  const raw = String(text || '');
  const re = /(\[!\[[^\]]*\]\([^)]+\)\]\([^)]+\)|!\[[^\]]*\]\([^)]+\))/g;
  if (!re.test(raw)) return null;
  re.lastIndex = 0;
  const parts = raw.split(re).filter((part) => part !== '');
  return parts;
}

function decodeHtmlCell(raw) {
  return String(raw || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]+>/g, '')
    .trim();
}

function parseHtmlTable(html) {
  const rows = [];
  const trRe = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
  let trMatch;
  while ((trMatch = trRe.exec(html)) !== null) {
    const cells = [];
    const cellRe = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
    let cellMatch;
    while ((cellMatch = cellRe.exec(trMatch[1])) !== null) {
      cells.push(decodeHtmlCell(cellMatch[1]));
    }
    if (cells.length) rows.push(cells);
  }
  if (!rows.length) return null;
  return {
    hasHeader: /<th[\s>]/i.test(html),
    rows,
  };
}

function splitByHtmlBlocks(markdown) {
  const text = String(markdown || '');
  const segments = [];
  const re = /(<table[\s\S]*?<\/table>|<iframe\b[^>]*>[\s\S]*?<\/iframe>)/gi;
  let lastIndex = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'md', content: text.slice(lastIndex, match.index) });
    }
    const block = match[0];
    segments.push({
      type: /^<table/i.test(block) ? 'html-table' : 'html-iframe',
      content: block,
    });
    lastIndex = match.index + block.length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'md', content: text.slice(lastIndex) });
  }
  if (!segments.length) {
    segments.push({ type: 'md', content: text });
  }
  return segments;
}

const ALLOWED_IFRAME_HOSTS = new Set([
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
]);

function parseMarkdownIframe(html) {
  const raw = String(html || '').trim();
  const match = raw.match(/<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/i);
  if (!match) return null;
  try {
    const url = new URL(match[1]);
    if (!ALLOWED_IFRAME_HOSTS.has(url.hostname) || !url.pathname.includes('/embed/')) {
      return null;
    }
    const titleMatch = raw.match(/\btitle=["']([^"']*)["']/i);
    return { src: url.toString(), title: titleMatch?.[1] || 'Vídeo' };
  } catch (_) {
    return null;
  }
}

function MarkdownIframeBlock({ src, title, blockKey }) {
  return (
    <div key={blockKey} className="rel-orya-md-iframe-wrap">
      <iframe
        src={src}
        title={title}
        className="rel-orya-md-iframe"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

function MarkdownImageBlock({ alt, src, href, blockKey }) {
  const img = (
    <img
      src={src}
      alt={alt || 'Imagem da documentação'}
      className="rel-orya-md-image"
      loading="lazy"
    />
  );

  return (
    <div key={blockKey} className="rel-orya-md-image-wrap">
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {img}
        </a>
      ) : (
        img
      )}
    </div>
  );
}

const MD_TABLE_LINE_PREFIX_RE = /^[-*•]\s+/;

function normalizeMarkdownTableLine(line) {
  return String(line || '').trim().replace(MD_TABLE_LINE_PREFIX_RE, '');
}

function splitMarkdownCells(line) {
  const trimmed = normalizeMarkdownTableLine(line);
  if (!trimmed.includes('|')) return null;
  const inner = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((c) => c.trim());
}

function isSeparatorRow(cells) {
  return cells?.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c));
}

function normCell(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function rowDuplicatesHeader(header, row) {
  if (!header?.length || !row?.length) return false;
  const matches = header.filter((h, i) => normCell(h) === normCell(row[i])).length;
  return matches >= Math.max(2, Math.ceil(header.length * 0.75));
}

function parseTableLines(lines, start) {
  const header = splitMarkdownCells(lines[start]);
  if (!header?.length || isSeparatorRow(header)) return null;

  let i = start + 1;
  if (i < lines.length) {
    const sep = splitMarkdownCells(lines[i]);
    if (sep && isSeparatorRow(sep)) i += 1;
  }

  const body = [];
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = normalizeMarkdownTableLine(line);
    if (!trimmed.startsWith('|')) break;
    const row = splitMarkdownCells(line);
    if (row && isSeparatorRow(row)) break;
    const colCount = header.length;
    const padded = row ? [...row] : [];
    while (padded.length < colCount) padded.push('');
    if (row && isCompleteTableRow(header, padded)) {
      body.push(padded);
    } else {
      break;
    }
    i += 1;
  }

  while (body.length && rowDuplicatesHeader(header, body[0])) {
    body.shift();
  }

  if (!body.length) return null;

  return { header, body, next: i };
}

function isLikelyMarkdownTable(lines, start) {
  const header = splitMarkdownCells(lines[start]);
  if (!header?.length || isSeparatorRow(header)) return false;

  let i = start + 1;
  if (i < lines.length) {
    const sep = splitMarkdownCells(lines[i]);
    if (sep && isSeparatorRow(sep)) return true;
  }

  while (i < lines.length) {
    const trimmed = normalizeMarkdownTableLine(lines[i]);
    if (!trimmed.startsWith('|')) break;
    const row = splitMarkdownCells(lines[i]);
    if (row && !isSeparatorRow(row) && row.length >= 1) return true;
    i += 1;
  }
  return false;
}

function parseHighlightStyle(styleStr) {
  const bg = String(styleStr || '').match(/background-color:\s*([^;]+)/i);
  if (!bg) return null;
  const color = bg[1].trim();
  if (!/^((#[\da-f]{3,8})|(rgba?\(\s*[\d.,\s%]+\s*\))|([a-z]+))$/i.test(color)) {
    return null;
  }
  return { backgroundColor: color };
}

function stripSimpleHtmlEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
}

function renderMarkdownEmphasis(part, keyPrefix) {
  const segments = String(part || '').split(/(\[[^\]]+\]\([^)]+\))/g);
  return segments.map((segment, sidx) => {
    const linkMatch = segment.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const label = linkMatch[1].trim();
      const href = linkMatch[2].trim();
      const text = label === href ? href : label;
      if (!/^https?:\/\//i.test(href)) return text;
      return (
        <a
          key={`${keyPrefix}-lnk-${sidx}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="rel-orya-md-link"
        >
          {text}
        </a>
      );
    }

    const mdParts = segment.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return mdParts.map((mdPart, midx) => {
      if (mdPart.startsWith('**') && mdPart.endsWith('**')) {
        return <strong key={`${keyPrefix}-b-${sidx}-${midx}`}>{mdPart.slice(2, -2)}</strong>;
      }
      if (mdPart.startsWith('*') && mdPart.endsWith('*') && mdPart.length > 2) {
        return <em key={`${keyPrefix}-i-${sidx}-${midx}`}>{mdPart.slice(1, -1)}</em>;
      }
      return mdPart;
    });
  });
}

function renderRichInline(text, keyPrefix = 'ri', { skipNumericFormat = false } = {}) {
  const raw = skipNumericFormat
    ? stripSimpleHtmlEntities(text)
    : formatOryaMarkdownTextForDisplay(stripSimpleHtmlEntities(text));
  if (!raw) return null;

  const richRe = /(<span\s+style="[^"]*"[^>]*>[\s\S]*?<\/span>|<iframe\b[^>]*>[\s\S]*?<\/iframe>)/gi;
  if (!richRe.test(raw)) {
    return renderMarkdownEmphasis(raw, keyPrefix);
  }
  richRe.lastIndex = 0;
  const parts = raw.split(richRe).filter((part) => part !== '');

  return parts.map((part, idx) => {
    const iframe = parseMarkdownIframe(part);
    if (iframe) {
      return (
        <MarkdownIframeBlock
          key={`${keyPrefix}-iframe-${idx}`}
          src={iframe.src}
          title={iframe.title}
          blockKey={`${keyPrefix}-iframe-${idx}`}
        />
      );
    }

    const spanMatch = part.match(/^<span\s+style="([^"]*)"[^>]*>([\s\S]*?)<\/span>$/i);
    if (spanMatch) {
      const style = parseHighlightStyle(spanMatch[1]);
      const inner = stripSimpleHtmlEntities(spanMatch[2]).replace(/<[^>]+>/g, '');
      if (style) {
        return (
          <span
            key={`${keyPrefix}-span-${idx}`}
            className="rel-orya-md-highlight"
            style={{ backgroundColor: style.backgroundColor }}
          >
            {renderMarkdownEmphasis(inner, `${keyPrefix}-span-${idx}`)}
          </span>
        );
      }
      return (
        <React.Fragment key={`${keyPrefix}-span-plain-${idx}`}>
          {renderMarkdownEmphasis(inner, `${keyPrefix}-sp-${idx}`)}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={`${keyPrefix}-t-${idx}`}>
        {renderMarkdownEmphasis(part, `${keyPrefix}-t-${idx}`)}
      </React.Fragment>
    );
  });
}

function renderRichCellContent(text, { skipNumericFormat = false } = {}) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const imageParts = extractMarkdownImages(raw);
  const chunks = imageParts || [raw];

  return (
    <div className="rel-orya-md-cell-rich">
      {chunks.map((part, idx) => {
        const trimmed = part.trim();
        if (!trimmed) return null;
        const image = parseMarkdownImage(trimmed);
        if (image) {
          return (
            <MarkdownImageBlock
              key={`cell-img-${idx}`}
              alt={image.alt}
              src={image.src}
              href={image.href}
              blockKey={`cell-img-${idx}`}
            />
          );
        }
        return trimmed.split(/\r?\n/).map((line, li) => {
          const lineText = line.trim();
          if (!lineText) return null;
          const lineImage = parseMarkdownImage(lineText);
          if (lineImage) {
            return (
              <MarkdownImageBlock
                key={`cell-line-img-${idx}-${li}`}
                alt={lineImage.alt}
                src={lineImage.src}
                href={lineImage.href}
                blockKey={`cell-line-img-${idx}-${li}`}
              />
            );
          }
          return (
            <div key={`cell-line-${idx}-${li}`} className="rel-orya-md-cell-line">
              {renderRichInline(lineText, `cell-${idx}-${li}`, { skipNumericFormat })}
            </div>
          );
        });
      })}
    </div>
  );
}

function OryaHtmlTable({ rows, blockKey }) {
  return (
    <div key={blockKey} className="rel-orya-md-table-shell rel-orya-md-table-shell--plain">
      <div className="rel-orya-md-table-wrap">
        <table className="rel-orya-md-table">
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{renderRichCellContent(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProseBlock({ lines, blockKey }) {
  return (
    <div key={blockKey} className="rel-orya-md-prose">
      {lines.map((line, li) => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        const iframe = parseMarkdownIframe(trimmed);
        if (iframe) {
          return (
            <MarkdownIframeBlock
              key={li}
              src={iframe.src}
              title={iframe.title}
              blockKey={`iframe-${li}`}
            />
          );
        }
        const image = parseMarkdownImage(trimmed);
        if (image) {
          return (
            <MarkdownImageBlock
              key={li}
              alt={image.alt}
              src={image.src}
              href={image.href}
              blockKey={`img-${li}`}
            />
          );
        }
        const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
          const level = Math.min(headingMatch[1].length, 4);
          return (
            <div key={li} className={`rel-orya-md-heading rel-orya-md-heading--h${level}`}>
              {renderRichInline(headingMatch[2], `h-${li}`)}
            </div>
          );
        }
        const isList = /^[-*•]\s+/.test(trimmed);
        const text = isList ? trimmed.replace(/^[-*•]\s+/, '') : trimmed;
        const { emoji, text: prose } = enrichOryaProseLine(text);
        return (
          <div
            key={li}
            className={`rel-orya-md-line${isList ? ' rel-orya-md-line--list' : ''}`}
          >
            {emoji && (
              <span className="rel-orya-md-emoji" aria-hidden>
                {emoji}
              </span>
            )}
            <span className="rel-orya-md-line-text">{renderRichInline(prose, `line-${li}`)}</span>
          </div>
        );
      })}
    </div>
  );
}

function processMarkdownLines(lines, blocks, options) {
  const { segmentKey, uniformTables, renderCell, onTableFound } = options;
  let i = 0;

  while (i < lines.length) {
    const iframe = parseMarkdownIframe(String(lines[i] || '').trim());
    if (iframe) {
      blocks.push(
        <MarkdownIframeBlock
          key={`${segmentKey}-iframe-${i}`}
          src={iframe.src}
          title={iframe.title}
          blockKey={`${segmentKey}-iframe-${i}`}
        />
      );
      i += 1;
      continue;
    }

    const image = parseMarkdownImage(lines[i]);
    if (image) {
      blocks.push(
        <MarkdownImageBlock
          key={`${segmentKey}-img-${i}`}
          alt={image.alt}
          src={image.src}
          href={image.href}
          blockKey={`${segmentKey}-img-${i}`}
        />
      );
      i += 1;
      continue;
    }

    const table = isLikelyMarkdownTable(lines, i) ? parseTableLines(lines, i) : null;
    if (table) {
      onTableFound();
      blocks.push(
        <RelatoriosOryaScrollableTable
          key={`${segmentKey}-t-${i}`}
          header={table.header}
          body={table.body}
          renderCell={renderCell}
          uniform={uniformTables}
        />
      );
      i = table.next;
      continue;
    }

    if (!lines[i].trim()) {
      i += 1;
      continue;
    }

    const para = [];
    while (i < lines.length && lines[i].trim() && !isLikelyMarkdownTable(lines, i)) {
      if (
        parseMarkdownImage(lines[i])
        || parseMarkdownIframe(String(lines[i] || '').trim())
        || /<table[\s>]/i.test(lines[i])
        || /<iframe\b/i.test(lines[i])
      ) {
        break;
      }
      if (normalizeMarkdownTableLine(lines[i]).startsWith('|')) break;
      para.push(lines[i]);
      i += 1;
    }
    if (para.length) {
      blocks.push(<ProseBlock key={`${segmentKey}-p-${i}`} lines={para} blockKey={`${segmentKey}-p-${i}`} />);
    } else {
      i += 1;
    }
  }
}

function RelatoriosOryaMarkdownView({
  markdown = '',
  canExport = true,
  onExportXlsx = null,
  exportFilename = 'orya-tabela.xlsx',
  compact = false,
}) {
  const renderCell = useCallback((text, columnHeader) => {
    const displayText =
      columnHeader != null ? formatOryaTableCellForDisplay(text, columnHeader) : text;
    return renderRichCellContent(displayText, { skipNumericFormat: true });
  }, []);
  const handleExportXlsx = useCallback(() => {
    if (onExportXlsx) {
      onExportXlsx();
      return;
    }
    downloadOryaMarkdownTableXlsx(markdown, exportFilename);
  }, [exportFilename, markdown, onExportXlsx]);
  const segments = splitByHtmlBlocks(markdown);
  const blocks = [];
  let hasTable = false;
  const tableCount = countMarkdownTables(markdown) + segments.filter((s) => s.type === 'html-table').length;
  const uniformTables = !compact && tableCount > 1;

  segments.forEach((segment, si) => {
    if (segment.type === 'html-iframe') {
      const iframe = parseMarkdownIframe(segment.content);
      if (iframe) {
        blocks.push(
          <MarkdownIframeBlock
            key={`iframe-${si}`}
            src={iframe.src}
            title={iframe.title}
            blockKey={`iframe-${si}`}
          />
        );
      }
      return;
    }

    if (segment.type === 'html-table') {
      const parsed = parseHtmlTable(segment.content);
      if (!parsed?.rows?.length) return;
      hasTable = true;
      if (parsed.hasHeader && parsed.rows.length > 1) {
        blocks.push(
          <RelatoriosOryaScrollableTable
            key={`html-t-${si}`}
            header={parsed.rows[0]}
            body={parsed.rows.slice(1)}
            renderCell={renderCell}
            uniform={uniformTables}
          />
        );
      } else {
        blocks.push(<OryaHtmlTable key={`html-t-${si}`} rows={parsed.rows} blockKey={`html-t-${si}`} />);
      }
      return;
    }

    const lines = String(segment.content || '').split(/\r?\n/);
    processMarkdownLines(lines, blocks, {
      segmentKey: `seg-${si}`,
      uniformTables,
      renderCell,
      onTableFound: () => {
        hasTable = true;
      },
    });
  });

  return (
    <div className={`rel-orya-md${compact ? ' rel-orya-md--compact' : ''}`}>
      {blocks}
      {hasTable && canExport && tableCount <= 1 ? (
        <button
          type="button"
          className="rel-orya-md-export-btn"
          onClick={handleExportXlsx}
          aria-label="Baixar planilha"
        >
          <FileSpreadsheet size={14} strokeWidth={2} aria-hidden />
          <span>Baixar planilha</span>
        </button>
      ) : null}
    </div>
  );
}

export default memo(RelatoriosOryaMarkdownView);
